import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import { connect } from "react-redux";
import React from "react";
import { bindActionCreators } from "redux";
import { formValueSelector } from "redux-form";
import Loader from "../spreedly-form/SpreedlyLoader";

import {
  getStripeClientSecretKey,
  getTotalAmount,
  getSelectedAllocations,
  getAllocationType,
  setStripeObject,
  setStripeElementsObject,
  formSubmitted,
  submittingForm,
} from "data/giving-form/allocation-selection";
import { getCampaign } from "data/parent-campaign";
import { getSpreedlyToken } from "data/spreedly";
import { getEnabledFeatures, getCurrencyCode } from "data/entity";
import { getBillingType, getPaymentInterval } from "data/giving-form/billing";
import DisplayError from "./StripeDisplayError";
import countriesCode from "../../../lib/utils/countriesCode";
import { FormattedMessage } from "react-intl";

const ELEMENT_OPTIONS = {
  style: {
    paymentRequestButton: {
      type: "donate",
      theme: "dark",
    },
  },
};

class StripeForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      paymentRequestLoading: false,
      showCVVHelp: false,
      canMakePayment: false,
      cardNumberErrorMessage: "",
      cardCvcErrorMessage: "",
      cardExpiryErrorMessage: "",
      loadingCardNumber: true,
      loadingCardCvc: true,
      loadingCardExpiry: true,
    };
  }

  componentDidUpdate() {
    const { stripe } = this.props;

    if (stripe && !this.paymentRequest) {
      this.createPaymentRequest(stripe);
    }
  }

  async createPaymentRequest(stripe) {
    this.initPaymentRequest(stripe);

    this.handlePaymentRequest(stripe);
    const canMakePayment = await this.paymentRequest.canMakePayment();

    canMakePayment
      ? this.setState({ canMakePayment: true })
      : this.setState({ canMakePayment: false });
  }

  async handlePaymentRequest(stripe) {
    const { billingType, dispatch, submittingForm } = this.props;

    this.paymentRequest.on("paymentmethod", async (event) => {
      this.setState({ paymentRequestLoading: true });
      dispatch(submittingForm());

      if (event && event.paymentMethod.billing_details) {
        const allValues = this.buildPaymentValues(event);

        if (billingType === "recurring") {
          await this.handleRecurringPaymentRequest(allValues, event);
        } else {
          await this.handleOneTimePaymentRequest(stripe, allValues, event);
        }
      }
    });
  }

  async handleRecurringPaymentRequest(allValues, paymentMethodEvent) {
    const { handleCreateStripeCustomer, dispatch } = this.props;

    paymentMethodEvent.complete("success");

    const skipCreatePaymentMethod = true;
    await handleCreateStripeCustomer(
      allValues.email,
      allValues,
      dispatch,
      paymentMethodEvent.paymentMethod.id,
      skipCreatePaymentMethod,
    );

    this.setState({ paymentRequestLoading: false });
  }

  async handleOneTimePaymentRequest(stripe, allValues, paymentMethodEvent) {
    const { stripeClientSecretKey, submitPaymentRequest, dispatch, createStripeIntent } =
      this.props;
    const clientSecretKey = !stripeClientSecretKey
      ? await createStripeIntent()
      : stripeClientSecretKey;

    const result = await stripe.confirmCardPayment(
      clientSecretKey,
      { payment_method: paymentMethodEvent.paymentMethod.id },
      { handleActions: false },
    );

    if (result.error) {
      paymentMethodEvent.complete("fail");
    } else {
      paymentMethodEvent.complete("success");

      if (result.paymentIntent.status === "succeeded") {
        allValues.stripe_payment_method_id = result.paymentIntent.payment_method;
        allValues.currency_code = result.paymentIntent.currency.toUpperCase();
        allValues.stripe_payment_intent_id = result.paymentIntent.id;
        allValues.result = result;
        allValues.succeeded = "succeeded";
        submitPaymentRequest(allValues, dispatch);
      }
    }

    this.setState({ paymentRequestLoading: false });
  }

  buildPaymentValues(paymentMethodEvent) {
    const { paymentInterval, occurrences, totalAmount, customFieldsHash } = this.props;

    const allValues = {
      address: {
        address1: paymentMethodEvent.paymentMethod.billing_details.address.line1,
        address2: paymentMethodEvent.paymentMethod.billing_details.address.line2,
        cell_phone: paymentMethodEvent.paymentMethod.billing_details.phone,
        city: paymentMethodEvent.paymentMethod.billing_details.address.city,
        country: paymentMethodEvent.paymentMethod.billing_details.address.country,
        zip: paymentMethodEvent.paymentMethod.billing_details.address.postal_code,
      },
      amount: totalAmount,
      first_name: paymentMethodEvent.paymentMethod.billing_details.name,
      isPublic: true,
      email: paymentMethodEvent.paymentMethod.billing_details.email,
      stripe_payment_method_id: paymentMethodEvent.paymentMethod.id,
      custom_fields_hash: customFieldsHash,
    };

    allValues.stripe_payment_method_type = "card_wallet";

    if (paymentInterval) {
      allValues.interval = paymentInterval;
    }

    if (paymentInterval === "monthly") {
      allValues.occurrences = parseFloat(occurrences) * 12;
    } else if (paymentInterval === "quarterly") {
      allValues.occurrences = parseFloat(occurrences) * 4;
    } else if (paymentInterval === "annually") {
      allValues.occurrences = parseFloat(occurrences) * 1;
    } else {
      // one time gift
      allValues.occurrences = null;
    }

    return allValues;
  }

  initPaymentRequest(stripe) {
    const { totalAmount, country, currencyCode } = this.props;

    const countryCode = countriesCode.filter(
      (countryCode) => countryCode.name === country,
    );

    this.paymentRequest = stripe.paymentRequest({
      country: countryCode && countryCode.length > 0 ? countryCode[0].code : "US",
      currency: currencyCode.toLowerCase(),
      total: {
        label: "Total amount",
        amount: parseInt(parseFloat(totalAmount) * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });
  }

  handleFailedValidation() {
    const { afterFailedStripeValidation } = this.props;

    afterFailedStripeValidation();
  }

  onReadyCardNumber(event) {
    this.setState((prev) => {
      return {
        loadingCardNumber: false,
      };
    });
  }

  onReadyCardCvc(event) {
    this.setState((prev) => {
      return {
        loadingCardCvc: false,
      };
    });
  }

  onReadyCardExpiry(event) {
    this.setState((prev) => {
      return {
        loadingCardExpiry: false,
      };
    });
  }

  render() {
    const {
      stripe,
      elements,
      setStripeObject,
      setStripeElementsObject,
      isFormInvalid,
      digitalWalletMode,
    } = this.props;
    const {
      paymentRequestLoading,
      canMakePayment,
      cardNumberErrorMessage,
      cardCvcErrorMessage,
      cardExpiryErrorMessage,
      loadingCardNumber,
      loadingCardCvc,
      loadingCardExpiry,
    } = this.state;
    setStripeObject(stripe);
    setStripeElementsObject(elements);

    return (
      <div>
        {!digitalWalletMode && (
          <div>
            <div className="row">
              <div className="col-xs-12 col-md-6 p-sm-b">
                <label className="required-field">
                  <FormattedMessage id="cardNumber" />
                </label>
                <Loader loading={loadingCardNumber} />
                <CardNumberElement
                  className={
                    loadingCardNumber
                      ? "hidden"
                      : "stripe-input border-grey-light black-font-color"
                  }
                  onChange={(event) =>
                    this.handleStripeElementError(event, "card-number")
                  }
                  onReady={(event) => this.onReadyCardNumber(event)}
                />
                <DisplayError error={cardNumberErrorMessage} />
              </div>
              <div className="col-xs-12 col-md-6 p-sm-b">
                <label className="required-field">
                  <FormattedMessage id="cvv" />
                  <a
                    data-testid="t-StripeF-a"
                    onClick={() => this.toggleShowCVVHelp()}
                    target="_blank">
                    <span
                      className="fa fa-fw fa-info-circle"
                      style={{ cursor: "pointer" }}
                    />
                  </a>
                </label>
                <Loader loading={loadingCardCvc} />
                <CardCvcElement
                  className={
                    loadingCardCvc
                      ? "hidden"
                      : "stripe-input border-grey-light black-font-color"
                  }
                  onChange={(event) => this.handleStripeElementError(event, "card-cvc")}
                  onReady={(event) => this.onReadyCardCvc(event)}
                />
                <DisplayError error={cardCvcErrorMessage} />
                {this.state.showCVVHelp ? this.renderCVVHelp() : ""}
              </div>
            </div>
            <div className="row">
              <div className="col-xs-12">
                <label htmlFor="donating_exp" className="required-field">
                  <FormattedMessage id="expirationDate" />
                </label>
              </div>
            </div>
            <div className="row">
              <div className="col-xs-12 col-md-12 m-sm-b">
                <Loader loading={loadingCardExpiry} />
                <CardExpiryElement
                  className={
                    loadingCardExpiry
                      ? "hidden"
                      : "stripe-input border-grey-light black-font-color"
                  }
                  onChange={(event) =>
                    this.handleStripeElementError(event, "card-expiry")
                  }
                  onReady={(event) => this.onReadyCardExpiry(event)}
                />
                <DisplayError error={cardExpiryErrorMessage} />
              </div>
            </div>
          </div>
        )}
        {canMakePayment && digitalWalletMode && (
          <div className="row">
            <div className="col-xs-12 col-md-6 p-sm-b payment-request-container">
              {!paymentRequestLoading && !isFormInvalid && (
                <PaymentRequestButtonElement
                  options={{
                    ...ELEMENT_OPTIONS,
                    paymentRequest: this.paymentRequest,
                  }}
                />
              )}
              {paymentRequestLoading && <Loader loading={paymentRequestLoading} />}
            </div>
          </div>
        )}
      </div>
    );
  }

  handleStripeElementError = (event, type) => {
    const { dispatch } = this.props;
    const hasError = event && event.error;
    if (hasError) {
      dispatch(formSubmitted());
    }

    switch (type) {
      case "card-number":
        this.setState({
          cardNumberErrorMessage: hasError ? event.error.message : "",
        });
        this.handleFailedValidation();
        return;
      case "card-cvc":
        this.setState({
          cardCvcErrorMessage: hasError ? event.error.message : "",
        });
        this.handleFailedValidation();
        return;
      case "card-expiry":
        this.setState({
          cardExpiryErrorMessage: hasError ? event.error.message : "",
        });
        this.handleFailedValidation();
        return;
      default:
        return;
    }
  };

  toggleShowCVVHelp() {
    this.setState({ showCVVHelp: !this.state.showCVVHelp });
  }
  renderCVVHelp() {
    return (
      <div>
        Security Code: 3 digits to the right of the signature on back of card, or 4 digits
        on front for AMEX.
      </div>
    );
  }
}

const selector = formValueSelector("donation");

const mapStateToProps = (state) => {
  return {
    currencyCode: getCurrencyCode(state),
    stripeClientSecretKey: getStripeClientSecretKey(state),
    totalAmount: getTotalAmount(state),
    campaignId: getCampaign(state)("giving_form").get("id"),
    selectedAllocations: getSelectedAllocations(state),
    allocationType: getAllocationType(state),
    spreedlyToken: getSpreedlyToken(state),
    paymentInterval: getPaymentInterval(state),
    isStripePayment: getEnabledFeatures(state).includes("stripe"),
    billingType: getBillingType(state),
    occurrences: selector(state, "occurrences"),
    country: selector(state, "address.country"),
    customFieldsHash: selector(state, "custom_fields_hash"),
  };
};

const mapActionCreators = (dispatch) => {
  return {
    dispatch,
    formSubmitted,
    submittingForm,
    ...bindActionCreators({ setStripeObject, setStripeElementsObject }, dispatch),
  };
};

export default connect(mapStateToProps, mapActionCreators)(StripeForm);
